import { Box, TextField } from '@material-ui/core'

import * as R from 'ramda'
import React, { FC } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import styled from 'styled-components'

import CategoryAutoCompleteSelector from '../../components/form/CategoryAutoCompleteSelector'
import FormMultiSelector from '../../components/form/MultiSelector'
import { MONTH } from '../../constants/enums/common.enum.constant'
import { ProductFormInterface } from '../../interfaces/product'
import { getEnumNonNumericValues } from '../../utils/enum.util'

const StyledLabel = styled.label`
  font-weight: 600;
  font-size: 16px;
`

const StyledLabelContainer = styled.div`
  width: 100%;
  margin-bottom: 15px;
`

const StyledFormFieldContainer = styled.div`
  margin: 15px 0px 15px 0px;
  padding: 0px;
`

const StyledErrorMessageContainer = styled.div`
  height: 20px;
  width: 100%;
  display: flex;
  align-items: center;
`

const FormLabel = ({ name }: { name: string }) => (
  <StyledLabelContainer>
    <StyledLabel>{name}</StyledLabel>
  </StyledLabelContainer>
)

const FormErrorMessage = ({ message }: { message: string }) => (
  <Box component="div" display="line" fontSize={10} color="red">
    {message}
  </Box>
)

type Props = {
  productDetail: ProductFormInterface
}

const CompanyProductFormMainBody: FC<Record<string, unknown> & Props> = ({
  productDetail
}: Props) => {
  const { errors, control } = useFormContext()
  const textFieldInputProps = { style: { fontSize: 13 } }

  return (
    <>
      <StyledFormFieldContainer>
        <FormLabel name="Product Name" />
        <Controller
          name="name"
          control={control}
          defaultValue={R.propOr('', 'name', productDetail)}
          as={
            <TextField
              id="outlined-required"
              variant="outlined"
              defaultValue={R.propOr('', 'name', productDetail)}
              inputProps={textFieldInputProps}
              fullWidth
            />
          }
        />
      </StyledFormFieldContainer>
      <StyledFormFieldContainer>
        <FormLabel name="Category" />
        <CategoryAutoCompleteSelector
          fieldName="categoryId"
          fieldValue={{
            categoryId: R.propOr('', 'categoryId', productDetail),
            name: R.pathOr('', ['category', 'name'], productDetail)
          }}
          required
        />
        <StyledErrorMessageContainer>
          {errors.categoryId && (
            <FormErrorMessage message={errors?.categoryId?.message as string} />
          )}
        </StyledErrorMessageContainer>
      </StyledFormFieldContainer>
      <StyledFormFieldContainer>
        <FormLabel name="HS Code" />
        <Controller
          name="hsCode"
          control={control}
          defaultValue={R.pathOr('', ['hsCode'], productDetail)}
          as={
            <TextField
              required
              variant="outlined"
              defaultValue={R.pathOr('', ['hsCode'], productDetail)}
              inputProps={textFieldInputProps}
              fullWidth
            />
          }
        />
      </StyledFormFieldContainer>
      <StyledFormFieldContainer>
        <FormLabel name="Brands" />
        <Controller
          name="brands"
          control={control}
          defaultValue={R.pathOr('', ['brands'], productDetail)}
          render={({ onChange }) => (
            <TextField
              onChange={onChange}
              required
              variant="outlined"
              defaultValue={R.pathOr('', ['brands'], productDetail)}
              inputProps={textFieldInputProps}
              fullWidth
            />
          )}
        />
      </StyledFormFieldContainer>
      <StyledFormFieldContainer>
        <FormLabel name="Seasons Available" />
        <FormMultiSelector
          fieldName="seasonsAvailable"
          selections={getEnumNonNumericValues(MONTH)}
          defaultValue={R.propOr([], 'seasonsAvailable', productDetail)}
          noSort
        />
      </StyledFormFieldContainer>
      <StyledFormFieldContainer>
        <FormLabel name="Packaging" />
        <Controller
          name="packaging"
          control={control}
          defaultValue={R.propOr('', 'packaging', productDetail)}
          render={({ onChange }) => (
            <TextField
              multiline
              rows={5}
              onChange={onChange}
              id="outlined"
              variant="outlined"
              defaultValue={R.propOr('', 'packaging', productDetail)}
              inputProps={textFieldInputProps}
              fullWidth
            />
          )}
        />
      </StyledFormFieldContainer>
    </>
  )
}

export default CompanyProductFormMainBody
